import React, { useEffect, useRef, useState } from 'react';

// Outside-click + Esc to close. Shared between FileMenu and DocumentToolsMenu.
function usePopover() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, wrapRef };
}

// File menu — mirrors the "File" dropdown in DocumentNavBar.js
// (New / Open / Save / Share, divider, Version History / Save Version /
// Download as Docx / Reformat Document / Print). Each item invokes the
// matching handler prop; the parent decides what's real and what's stubbed.
//
// Sits in the document toolbar at the very left, anchored just before the
// heading-size dropdown so it reads as a file-menu in a typical word
// processor toolbar layout.
export default function FileMenu({
  onNewDocument,
  onOpenDocument,
  onSaveDocument,
  onShare,
  onVersionHistory,
  onSaveVersion,
  onDownloadDocx,
  onReformat,
  onPrint,
  canSaveVersion = true,
  readOnly = false,
  disabled = false,
}) {
  const { open, setOpen, wrapRef } = usePopover();

  const item = (extra = '') =>
    `w-full text-left inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${extra}`;

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        disabled={disabled}
        className={[
          'text-xs font-medium px-2 py-1 rounded flex items-center gap-1 border whitespace-nowrap shrink-0',
          'border-slate-200 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-600 dark:text-slate-300',
          disabled ? 'opacity-40 cursor-not-allowed' : '',
        ].join(' ')}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        File
        <svg
          aria-hidden="true"
          className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-2 z-30 w-60 rounded-xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-lg p-1.5"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onNewDocument?.(); }}
            className={item('text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/60')}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
            New Document
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onOpenDocument?.(); }}
            className={item('text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/60')}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-7l-2-2H5a2 2 0 00-2 2z" /></svg>
            Open
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onSaveDocument?.(); }}
            disabled={readOnly}
            title={readOnly ? 'Document is in read-only mode' : 'Force-save current changes'}
            className={item('text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-500/[0.12]')}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
            Save
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onShare?.(); }}
            className={item('text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-500/[0.12]')}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
            Share
          </button>
          <div className="my-1 h-px bg-slate-200 dark:bg-slate-800/60" />
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onVersionHistory?.(); }}
            className={item('text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/[0.12]')}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Version History
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onSaveVersion?.(); }}
            disabled={!canSaveVersion || readOnly}
            title={readOnly ? 'Document is in read-only mode' : 'Save the current state as a named version'}
            className={item('text-yellow-700 dark:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-500/[0.12]')}
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" stroke="currentColor" strokeWidth="0" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
            Save Version
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onDownloadDocx?.(); }}
            className={item('text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-500/[0.12]')}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            Download as .docx
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onReformat?.(); }}
            disabled={readOnly}
            title={readOnly ? 'Document is in read-only mode' : 'Reapply template formatting'}
            className={item('text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/[0.12]')}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Reformat Document
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onPrint?.(); }}
            className={item('text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/60')}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
            Print
          </button>
        </div>
      )}
    </div>
  );
}
